import React, { useMemo, useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  ArrowDownRight, ArrowRight, CalendarCheck, Check, ChevronDown,
  Clock3, MessageCircle, PhoneCall, ShoppingCart, Star, Users, Zap
} from "lucide-react";
import "../styles/OpExEngage.css";

const RED = "#E60728";
const TIDYCAL_URL = "https://tidycal.com/dbenson";

const agents = [
    {
        n: "01", title: "Lead Reactivation", body: "Most leads go cold after 48 hours, so this agent automatically sends personalised, human‑sounding follow‑ups that revive your dormant database and convert 3–10% of old leads without any manual effort.", icon: Users },
    {
        n: "02", title: "Speed-to-Lead", body: "Because waiting more than five minutes can drop conversion by up to 80%, this agent replies instantly, 24/7, qualifying prospects and booking appointments to lift conversion and stop revenue leaking at the top of the funnel.", icon: Zap },
    {
        n: "03", title: "Cart Recovery", body: "With 60–75% of carts abandoned, this agent runs personalised multi‑step recovery sequences that bring shoppers back to checkout and typically recover 10–25% of lost carts.", icon: ShoppingCart },
    {
        n: "04", title: "Social Response", body: "Slow replies damage reputation and cost sales, so this agent monitors and responds to DMs and comments in real time, boosting engagement while reducing staff workload and strengthening brand perception.", icon: MessageCircle },
    {
        n: "05", title: "Testimonials", body: "Most brands struggle to gather consistent proof, so this agent requests, rewrites, formats and publishes testimonials automatically, creating a steady flow of conversion‑lifting social proof.", icon: Star },
    {
        n: "06", title: "Live Voice", body: "Missed calls mean missed revenue, so this human‑like voice agent answers, qualifies, books and updates your CRM around the clock — in English, Cantonese and Mandarin — while routing complex calls to staff and ensuring no opportunity is lost.", icon: PhoneCall },
];

const conversations = [
  { from: "out", text: "Hi it’s Ruby from OpEx Engage, is this the same Alex that was interested in reengaging old leads a while ago?" },
  { from: "in", text: "Hi, yes it is" },
  { from: "out", text: "Ok, my manager asked me to reach out but I hate annoying people with unwanted phone calls. Are you still interested in reengaging old leads?" },
  { from: "in", text: "Yes, but I am nervous about using AI for my business" },
  { from: "out", text: "That’s fair. A lot of people feel the same at first. The system just handles the first conversations in your voice so your team only speaks to people who are actually ready. You stay in control the whole time." },
  { from: "in", text: "Okay that makes sense" },
  { from: "out", text: "Would a quick 15-minute call this week help? I can show you exactly how it works with a couple of real examples." },
  { from: "in", text: "Yes, Thursday afternoon works for me" },
  { from: "out", text: "Perfect. I’ve booked you for Thursday at 3 pm. You’ll get a calendar invite shortly. Looking forward to it." },
];

const money = (n) => new Intl.NumberFormat("en-HK", {
    style: "currency",
    currency: "HKD",
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: 0
}).format(Number.isFinite(n) ? n : 0);

function RevenueCalculator({ onBook }) {
  const [leads, setLeads] = useState(3000);
  const [deal, setDeal] = useState(15000);
  const [reply, setReply] = useState(80);
  const [booking, setBooking] = useState(10);
  const [close, setClose] = useState(80);
  const rows = [
    { label: "Dormant leads", value: leads, set: setLeads, min: 500, max: 50000, step: 500, format: (v) => Number(v) },
    { label: "Average deal value ($)", value: deal, set: setDeal, min: 500, max: 1000000, step: 1000, format: (v) => Number(v) },
    { label: "Reply rate", value: reply, set: setReply, min: 10, max: 100, step: 1, suffix: "%" },
    { label: "Booking rate of replies", value: booking, set: setBooking, min: 10, max: 100, step: 1, suffix: "%" },
    { label: "Close rate of bookings", value: close, set: setClose, min: 10, max: 100, step: 1, suffix: "%" },
  ];
  const result = useMemo(() => {
    const replies = Math.round(leads * reply / 100);
    const bookings = Math.round(replies * booking / 100);
    const customers = Math.round(bookings * close / 100);
    return { replies, bookings, customers, revenue: customers * deal };
  }, [leads, deal, reply, booking, close]);
  return (
    <section className="oe-section oe-calc-section" id="revenue-calculator">
      <div className="oe-wrap">
        <div className="oe-eyebrow">MODEL THE OPPORTUNITY</div>
        <h2>What could your dormant list be worth?</h2>
        <p className="oe-intro">Adjust the assumptions to estimate potential closed revenue from your existing contacts.</p>
        <div className="oe-calc-card">
          <div className="oe-calc-inputs">
            {rows.map((row) => (
              <div className="oe-range-row" key={row.label}>
                <div className="oe-range-head">
                  <label htmlFor={`oe-${row.label}`}>{row.label}</label>
                  <div className="oe-number-wrap">
                    <input
                      id={`oe-${row.label}`}
                      type="number"
                      min={row.min}
                      max={row.max}
                      step={row.step}
                      value={row.value}
                      onChange={(e) => {
                        const next = e.target.value === "" ? row.min : Number(e.target.value);
                        row.set(Math.min(row.max, Math.max(row.min, next)));
                      }}
                      aria-label={`${row.label} value`}
                    />
                    <span>{row.suffix || ""}</span>
                  </div>
                </div>
                <input
                  className="oe-slider"
                  type="range"
                  min={row.min}
                  max={row.max}
                  step={row.step}
                  value={row.value}
                  onChange={(e) => row.set(Number(e.target.value))}
                  aria-label={`${row.label} slider`}
                />
                <div className="oe-range-limits"><span>{row.suffix ? `${row.min}%` : row.format(row.min)}</span><span>{row.suffix ? `${row.max}%` : row.format(row.max)}</span></div>
              </div>
            ))}
          </div>
          <div className="oe-calc-result" aria-live="polite">
            <span className="oe-result-label">Illustrative potential revenue</span>
            <strong>{money(result.revenue)}</strong>
            <span className="oe-result-sub">Based on the assumptions you selected</span>
            <div className="oe-breakdown">
              <div><span>Replies</span><strong>{result.replies.toLocaleString("en-HK")}</strong></div>
              <div><span>Bookings</span><strong>{result.bookings.toLocaleString("en-HK")}</strong></div>
              <div><span>Customers</span><strong>{result.customers.toLocaleString("en-HK")}</strong></div>
              <div><span>Revenue</span><strong>{money(result.revenue)}</strong></div>
            </div>
          </div>
        </div>
        <p className="oe-disclaimer">Illustrative only. Actual results depend on list quality, offer and industry.</p>
        <div className="oe-calc-end">
          <p>Most clients start with Lead Reactivation on a pure performance basis.</p>
          <button className="oe-btn oe-btn-primary" onClick={onBook}>Book a 15-minute demo <ArrowRight size={16} /></button>
        </div>
      </div>
    </section>
  );
}

function ConversationDemo() {
  const [shown, setShown] = useState(0);
  const [playing, setPlaying] = useState(false);
  const chatThreadRef = useRef(null);

  const visible = conversations.slice(0, shown);

  const startDemo = () => {
    setShown(0);
    setPlaying(true);

    let i = 0;

    const next = () => {
      i += 1;
      setShown(i);

      if (i < conversations.length) {
        window.setTimeout(
          next,
          conversations[i]?.from === "out" ? 2200 : 1700
        );
      } else {
        setPlaying(false);
      }
    };

    window.setTimeout(next, 700);
  };

  // Automatically play when the component loads.
  useEffect(() => {
    startDemo();
  }, []);

  // Scroll to the newest message as the conversation progresses.
  useEffect(() => {
    const thread = chatThreadRef.current;

    if (thread) {
      thread.scrollTo({
        top: thread.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [shown]);

  return (
    <div className="oe-phone-demo">
      <div className="oe-phone">
        <div className="oe-phone-screen">
          <div className="oe-phone-status">
            <span>9:41</span>
            <span>●●● ▰</span>
          </div>

          <div className="oe-chat-header">
            <div className="oe-contact-avatar">A</div>
            <strong>Alex</strong>
            <span>iMessage</span>
          </div>

          <div
            className="oe-chat-thread"
            ref={chatThreadRef}
            aria-live="polite"
          >
            {visible.map((message, i) => (
              <div
                key={i}
                className={`oe-bubble ${
                  message.from === "out" ? "oe-out" : "oe-in"
                }`}
              >
                {message.text}
              </div>
            ))}
            {playing &&
              shown < conversations.length &&
              conversations[shown]?.from === "out" && (
                <div className="oe-typing">
                  <i></i>
                  <i></i>
                  <i></i>
                </div>
              )}

            {!playing && shown === conversations.length && (
              <div className="oe-booked">
                <CalendarCheck size={14} />
                Appointment booked
              </div>
            )}
          </div>

          <div className="oe-message-composer">
            <span>iMessage</span>
            <span>↑</span>
          </div>
        </div>
      </div>

      <div className="oe-demo-controls">
        <p>A sample lead conversation, from first contact to booked meeting.</p>
      </div>
    </div>
  );
}

function DemoForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", website: "", list_size: "", has_phone_numbers: "",
    software: "", goal: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
			const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

			if (!API_BASE_URL) {
			  throw new Error("REACT_APP_BACKEND_URL is not configured");
			}

			await axios.post(`${API_BASE_URL}/api/engage-demo`, form);
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "We could not submit the form. Please try again or email info@opexcellence.hk.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="oe-section oe-booking-section" id="book-demo">
      <div className="oe-wrap oe-booking-grid">
        <div className="oe-booking-copy">
          <div className="oe-eyebrow">BOOK A DEMO</div>
          <h2>Book your OpEx Engage demo</h2>
          <p>Tell us a little about your business. We will prepare a relevant walkthrough and pilot discussion.</p>
          <div className="oe-booking-note"><Check size={17} /><span>We use your information to prepare your demo and build a relevant pilot. No spam.</span></div>
        </div>
        <div className="oe-form-card">
          {!submitted ? (
            <form onSubmit={submit}>
              <div className="oe-form-grid">
                <div className="oe-field"><label htmlFor="oe-name">Full Name *</label><input id="oe-name" name="name" value={form.name} onChange={update} required maxLength="120" autoComplete="name" /></div>
                <div className="oe-field"><label htmlFor="oe-email">Email *</label><input id="oe-email" name="email" type="email" value={form.email} onChange={update} required autoComplete="email" maxLength="254" /></div>
                <div className="oe-field"><label htmlFor="oe-phone">Phone *</label><input id="oe-phone" name="phone" type="tel" value={form.phone} onChange={update} required autoComplete="tel" maxLength="40" /></div>
                <div className="oe-field"><label htmlFor="oe-website">Website (We use this to build the bot!) *</label><input id="oe-website" name="website" type="url" placeholder="https://" value={form.website} onChange={update} required maxLength="300" /></div>
                <div className="oe-field"><label htmlFor="oe-list-size">How big is your email list? *</label><select id="oe-list-size" name="list_size" value={form.list_size} onChange={update} required><option value="">Choose a range</option><option>Under 1,000</option><option>1,000–5,000</option><option>5,000–20,000</option><option>20,000+</option></select></div>
                <div className="oe-field"><label htmlFor="oe-phone-list">Do you have phone numbers of the contacts? *</label><select id="oe-phone-list" name="has_phone_numbers" value={form.has_phone_numbers} onChange={update} required><option value="">Choose one</option><option>Yes</option><option>Partial</option><option>No</option></select></div>
                <div className="oe-field oe-field-full"><label htmlFor="oe-software">What software are you currently using for outreach and lead management? *</label><textarea id="oe-software" name="software" value={form.software} onChange={update} required maxLength="2000" rows="3" /></div>
                <div className="oe-field oe-field-full"><label htmlFor="oe-goal">What’s the number 1 thing you would like to gain from working with OpEx Engage? *</label><textarea id="oe-goal" name="goal" value={form.goal} onChange={update} required maxLength="2000" rows="3" /></div>
              </div>
              {error && <p className="oe-form-error" role="alert">{error}</p>}
              <button className="oe-btn oe-btn-primary oe-submit" type="submit" disabled={busy}>{busy ? "Submitting…" : "Continue to calendar"} <ArrowRight size={16} /></button>
              <p className="oe-privacy">We use the information only to prepare your demo and build a relevant pilot. No spam.</p>
            </form>
          ) : (
            <div className="oe-thanks">
              <div className="oe-thanks-icon"><Check size={22} /></div>
              <h3>Thank you. Your details have been sent.</h3>
              <p>Choose a suitable time below to complete your booking.</p>
              <a className="oe-btn oe-btn-primary" href={TIDYCAL_URL} target="_blank" rel="noreferrer">Open booking calendar <ArrowRight size={16} /></a>
              <div className="oe-tidycal">
                <iframe title="Book an OpEx Engage demo" src={TIDYCAL_URL} loading="lazy" />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function OpExEngage() {
  const scrollToBooking = () => document.getElementById("book-demo")?.scrollIntoView({ behavior: "smooth", block: "start" });
  const scrollToOffer = () => document.getElementById("lead-reactivation")?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <main className="oe-page">
      <section className="oe-hero">
        <div className="oe-wrap oe-hero-grid">
          <div className="oe-hero-copy">
            <div className="oe-eyebrow">OPEX ENGAGE</div>
            <h1>Recover lost revenue. Increase conversion. Reduce workload.</h1>
                      <p className="oe-hero-sub">OpEx Engage gives you six AI agents that work all day, every day, across the channels your customers already use. They reply instantly, bring back lost leads, recover abandoned carts, handle social messages, collect testimonials and even answer calls. Everything runs automatically, helping you capture more revenue while doing far less manual work.</p>
            <div className="oe-hero-actions"><button className="oe-btn oe-btn-primary" onClick={scrollToBooking}>Book a demo <ArrowRight size={16} /></button></div>
          </div>
          <ConversationDemo />
        </div>
      </section>

      <section className="oe-section oe-problem-section">
        <div className="oe-wrap">
          <div className="oe-eyebrow">THE PROBLEM · TODAY</div><h2>Thousands of missed opportunities</h2>
          <div className="oe-grid oe-grid-five">
            {[
              ["01", "Leads that went cold", "Interest existed. Nobody followed up in time."],
              ["02", "Enquiries answered too slowly", "The prospect had already moved on."],
              ["03", "Abandoned shopping carts", "Intent to buy, no nudge to finish."],
              ["04", "Social messages left unseen", "DMs and comments sitting unanswered."],
              ["05", "Testimonials never collected", "Happy customers, no social proof."],
            ].map(([n, title, body]) => <article className="oe-card" key={n}><span className="oe-card-number">{n}</span><h3>{title}</h3><p>{body}</p></article>)}
          </div>
                  <div className="oe-ink-band">The business itself is sound. The real constraint is human capacity,  teams can’t match the volume, speed, and consistency modern customers demand.</div>
        </div>
      </section>

          <section className="oe-section oe-mist-section">
        <div className="oe-wrap">
          <div className="oe-eyebrow">THE SOLUTION</div><h2>Six specialised agents. One system.</h2>
          <div className="oe-grid oe-grid-three">{agents.map(({n,title,body,icon:Icon})=><article className="oe-card oe-agent-card" key={n}><span className="oe-card-number">{n}</span><Icon className="oe-agent-icon" size={23} strokeWidth={1.6}/><h3>{title}</h3><p>{body}</p></article>)}</div>
          <p className="oe-channels">Voice <i /> WhatsApp <i /> Facebook <i /> Instagram <i /> SMS <i /> Email</p>
        </div>
      </section>

      <section className="oe-section">
        <div className="oe-wrap">
          <div className="oe-eyebrow">HOW IT WORKS</div><h2>We install it. We run it. You take the qualified conversations.</h2>
          <div className="oe-steps">
            <article><span>01</span><div><h3>You hand us the list or connect the channels</h3><p>Cold leads, enquiries, abandoned carts, social accounts or phone lines. We help prepare the data, configure workflows and establish the required permissions and compliance checks.</p></div></article>
            <article><span>02</span><div><h3>Agents detect, engage and follow up 24/7</h3><p>Personalised messages and calls in your brand voice. Agents answer questions, handle “not right now”, qualify prospects and follow up across connected channels.</p></div></article>
            <article><span>03</span><div><h3>Qualified results land where you work</h3><p>Booked appointments, recovered carts, testimonial requests or CRM-ready leads flow into the agreed calendar and systems. Your team can focus on the conversations that need them.</p></div></article>
          </div>
          <p className="oe-section-takeaway">Designed to launch in days, subject to integrations, data readiness and approvals.</p>
        </div>
      </section>

      <section className="oe-section oe-performance" id="lead-reactivation">
        <div className="oe-wrap oe-performance-inner">
          <div><div className="oe-eyebrow">START HERE</div><h2>Lead Reactivation on performance</h2><p className="oe-performance-lead">Turn a dormant database into potential revenue with no monthly fee.</p><p>We operate on a performance basis. We earn only when you earn, under agreed commercial terms.</p><p className="oe-performance-note">Many clients start here, measure the results, then consider Speed-to-Lead, Social Response or Live Voice.</p><button className="oe-btn oe-btn-primary" onClick={scrollToBooking}>Book a demo to see your list potential <ArrowRight size={16}/></button></div>
          <div className="oe-performance-aside"><span className="oe-aside-mark">01</span><h3>Start with the list you already have.</h3><p>Revisit existing demand before spending more to acquire new leads.</p><span className="oe-aside-line"></span><p className="oe-aside-small">Scope, eligibility, permissions and performance terms are agreed before launch.</p></div>
        </div>
      </section>

      <RevenueCalculator onBook={scrollToBooking}/>

      <DemoForm />

    </main>
  );
}
